import { useState, useEffect, useCallback } from 'react';

export interface ShareTargetData {
  url: string;
  title?: string;
  text?: string;
}

/**
 * Hook to detect when the app was opened via Web Share Target API.
 * Reads URL params and clears them from browser URL to prevent
 * reopening the modal on refresh.
 * Returns clearShareData to reset state after opening modal - prevents loop when modal closes.
 */
export const useShareTarget = (): {
  shareData: ShareTargetData | null;
  clearShareData: () => void;
} => {
  const [shareData, setShareData] = useState<ShareTargetData | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const url = params.get('url');
    const title = params.get('title');
    const text = params.get('text');

    // Check if we have share data
    if (url || text) {
      // Some apps share the URL in the "text" param instead of "url"
      const sharedUrl = url || extractUrlFromText(text);

      if (sharedUrl) {
        setShareData({
          url: sharedUrl,
          title: title || undefined,
          text: text || undefined,
        });

        // Clean up the URL to prevent reopening modal on refresh
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, []);

  const clearShareData = useCallback(() => {
    setShareData(null);
  }, []);

  return { shareData, clearShareData };
};

/**
 * Extract URL from text content (some apps like Instagram put the URL in the text param)
 */
const extractUrlFromText = (text: string | null): string | null => {
  if (!text) return null;

  // Try to find a URL in the text - common formats
  const urlRegex = /(https?:\/\/[^\s]+)/gi;
  const match = text.match(urlRegex);

  return match ? match[0].replace(/[.,;:!?)\]]+$/, '') : null; // trim trailing punctuation
};

